import {
    CalendarDate,
    PlacementId,
    CommunityId,
    DEFAULT_TOUR_DURATION_MINUTES,
    formatDuration,
    genEntityId,
    getCurrentTimezone,
    TOUR_TIME_GRANULARITY_MINUTES,
    TourId,
    UserId,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { ClientLink, CommunityLink } from "components/ui/entity-link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { Toggle } from "components/ui/toggle";
import { produce } from "immer";
import React, { useCallback, useEffect, useReducer } from "react";
import { useCurrentUserId } from "store/selectors/hooks";
import { TourDto } from "./types";

interface TourEditorProps {
    tour: TourDto | null;
    placementId?: PlacementId | null;
    communityId: CommunityId;
    communityName?: string;
    clientName?: string;
    onSaved: () => void;
    onClose: () => void;
}

interface FieldUpdate {
    field: keyof TourDto;
    value: string | null;
}

const DURATION_OPTIONS_MINUTES = [15, 30, 45, 60, 90, 120];

const buildDurationOptions = (currentMinutes: number): number[] => {
    if (DURATION_OPTIONS_MINUTES.includes(currentMinutes)) {
        return DURATION_OPTIONS_MINUTES;
    }
    return [...DURATION_OPTIONS_MINUTES, currentMinutes].sort((a, b) => a - b);
};

interface ComponentState {
    status: "loading" | "saving" | "loaded" | "failed";
    tour: TourDto | null;
    tourExists: boolean;
    pendingUpdates: FieldUpdate[];
    hasSaved: boolean;
    closeRequested: boolean;
    errorMessage: string;
}

type Action =
    | { type: "setTour"; tour: TourDto }
    | { type: "enqueueUpdate"; fieldUpdates: FieldUpdate[] }
    | { type: "startCreate" }
    | { type: "startUpdate" }
    | { type: "requestClose" }
    | { type: "setError"; message: string }
    | { type: "updateField"; field: keyof TourDto; value: string | null };

const defaultErrorMessage = "The tour failed to save.";

const reducer: React.Reducer<ComponentState, Action> = (p, a): ComponentState => {
    return produce(p, draft => {
        switch (a.type) {
            case "setTour": {
                draft.tour = a.tour;
                draft.status = "loaded";
                draft.tourExists = true;
                break;
            }
            case "enqueueUpdate": {
                draft.pendingUpdates.push(...a.fieldUpdates);
                break;
            }
            case "startCreate": {
                draft.status = "saving";
                draft.hasSaved = true;
                break;
            }
            case "startUpdate": {
                draft.status = "saving";
                draft.pendingUpdates = [];
                draft.hasSaved = true;
                break;
            }
            case "requestClose": {
                draft.closeRequested = true;
                break;
            }
            case "setError": {
                draft.status = "failed";
                draft.errorMessage = a.message || defaultErrorMessage;
                break;
            }
            case "updateField": {
                if (draft.tour) {
                    if (a.field === "tourDuration") {
                        draft.tour.tourDuration = Number(a.value);
                    } else {
                        (draft.tour as Record<string, unknown>)[a.field] = a.value;
                    }
                }
                break;
            }
        }
    });
};

const emptyTour = (
    placementId: PlacementId | null,
    communityId: CommunityId,
    userId: UserId,
): TourDto => {
    const now = new Date();
    const todayDate = CalendarDate.fromLocalDate(now);
    return {
        id: genEntityId<TourId>(),
        placementId,
        clientName: null,
        communityId,
        communityName: null,
        userId,
        tourDate: todayDate.toString(),
        tourTime: null,
        tourDuration: DEFAULT_TOUR_DURATION_MINUTES,
        tourTimeZone: null,
        privateNotes: null,
        familyNotes: null,
        showOnFamilyView: true,
        dateAdded: now.toISOString(),
        updatedAt: now.toISOString(),
    };
};

const initialState = (
    tour: TourDto | null,
    placementId: PlacementId | null,
    communityId: CommunityId,
    userId: UserId,
): ComponentState => {
    return {
        status: "loaded",
        tour: tour ?? emptyTour(placementId, communityId, userId),
        tourExists: !!tour,
        pendingUpdates: [],
        hasSaved: false,
        closeRequested: false,
        errorMessage: "",
    };
};

export const TourEditor: React.FC<TourEditorProps> = props => {
    const { tour, communityId, communityName, clientName, onSaved, onClose } = props;
    const placementId = props.placementId ?? null;
    const currentUserId = useCurrentUserId();

    const [state, dispatch] = useReducer(
        reducer,
        initialState(tour, placementId, communityId, currentUserId),
    );

    const currentDurationMinutes =
        state.tour?.tourDuration ?? DEFAULT_TOUR_DURATION_MINUTES;
    const durationOptions = buildDurationOptions(currentDurationMinutes);

    const trpcClient = trpc.useTRPC();

    const createMutation = useMutation({
        ...trpcClient.tours.create.mutationOptions(),
        onSuccess: data => {
            dispatch({ type: "setTour", tour: data });
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const updateMutation = useMutation({
        ...trpcClient.tours.update.mutationOptions(),
        onSuccess: data => {
            dispatch({ type: "setTour", tour: data });
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const deleteMutation = useMutation({
        ...trpcClient.tours.delete.mutationOptions(),
        onSuccess: () => {
            onSaved();
            onClose();
        },
        onError: error => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
    });

    const doUpdate = useCallback(
        (tourId: TourId, updatesToMake: FieldUpdate[]) => {
            dispatch({ type: "startUpdate" });

            const params = updatesToMake.reduce(
                (agg, update) => {
                    if (update.field === "tourDate") {
                        return {
                            ...agg,
                            tourDate: update.value as string,
                            tourTimeZone: getCurrentTimezone(),
                        };
                    }
                    if (update.field === "tourTime") {
                        return {
                            ...agg,
                            tourTime: update.value as string | null,
                            tourTimeZone: getCurrentTimezone(),
                        };
                    }
                    if (update.field === "tourDuration") {
                        return {
                            ...agg,
                            tourDuration: Number(update.value),
                        };
                    }
                    if (update.field === "privateNotes") {
                        return { ...agg, privateNotes: update.value as string };
                    }
                    if (update.field === "familyNotes") {
                        return { ...agg, familyNotes: update.value as string };
                    }
                    if (update.field === "showOnFamilyView") {
                        return {
                            ...agg,
                            showOnFamilyView: update.value === "true",
                        };
                    }
                    if (update.field === "userId") {
                        return {
                            ...agg,
                            userId: update.value as UserId,
                        };
                    }
                    return agg;
                },
                { id: tourId },
            );

            updateMutation.mutate(params);
        },
        [updateMutation],
    );

    const handleFieldChange = useCallback(
        (field: keyof TourDto, value: string | null) => {
            dispatch({ type: "updateField", field, value });

            const fieldUpdate: FieldUpdate = { field, value };

            if (state.status === "saving") {
                dispatch({ type: "enqueueUpdate", fieldUpdates: [fieldUpdate] });
            } else if (state.status === "loaded") {
                if (state.tourExists && state.tour) {
                    const updatesToMake = [...state.pendingUpdates, fieldUpdate];
                    doUpdate(state.tour.id, updatesToMake);
                } else {
                    const resolvedTourDate =
                        field === "tourDate"
                            ? (value as string)
                            : (state.tour?.tourDate ?? "");

                    if (!resolvedTourDate) {
                        return;
                    }

                    dispatch({ type: "startCreate" });
                    dispatch({ type: "enqueueUpdate", fieldUpdates: [fieldUpdate] });
                    createMutation.mutate({
                        communityId,
                        placementId,
                        userId: (state.tour?.userId as UserId) ?? currentUserId,
                        tourDate: resolvedTourDate,
                        tourTime:
                            field === "tourTime"
                                ? (value as string | null)
                                : state.tour?.tourTime,
                        tourDuration:
                            field === "tourDuration"
                                ? Number(value)
                                : (state.tour?.tourDuration ??
                                  DEFAULT_TOUR_DURATION_MINUTES),
                        privateNotes:
                            field === "privateNotes"
                                ? (value as string)
                                : state.tour?.privateNotes,
                        familyNotes:
                            field === "familyNotes"
                                ? (value as string)
                                : state.tour?.familyNotes,
                        tourTimeZone: getCurrentTimezone(),
                        showOnFamilyView:
                            field === "showOnFamilyView"
                                ? value === "true"
                                : (state.tour?.showOnFamilyView ?? true),
                    });
                }
            }
        },
        [
            state.status,
            state.tourExists,
            state.tour,
            state.pendingUpdates,
            communityId,
            placementId,
            currentUserId,
            createMutation,
            doUpdate,
        ],
    );

    const handleDelete = useCallback(() => {
        if (state.tour && state.tourExists) {
            deleteMutation.mutate({ id: state.tour.id });
        }
    }, [state.tour, state.tourExists, deleteMutation]);

    const handleClose = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            dispatch({ type: "requestClose" });
        },
        [dispatch],
    );

    useEffect(() => {
        if (state.status === "loaded" && state.tour && state.hasSaved) {
            if (state.pendingUpdates.length > 0) {
                doUpdate(state.tour.id, state.pendingUpdates);
            } else {
                onSaved();
            }
        }
    }, [state.tour]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            state.closeRequested &&
            state.status === "loaded" &&
            state.pendingUpdates.length === 0
        ) {
            onClose();
        }
        if (state.closeRequested && state.status === "failed") {
            onClose();
        }
    }, [state.closeRequested, state.status, state.pendingUpdates.length, onClose]);

    return (
        <Panel type={PanelType.medium} headerText="" onDismiss={onClose} isOpen={true}>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                        {state.tourExists ? "Edit Tour" : "Add Tour"}
                    </h2>
                    {state.tourExists ? (
                        <DeleteButton
                            backgroundColor="bg-white"
                            onClick={handleDelete}
                            confirm={{
                                title: "Delete Tour",
                                message: "Are you sure you want to delete this tour?",
                            }}
                        />
                    ) : null}
                </div>
                <div className="space-y-2">
                    {communityName ? (
                        <div>
                            <CommunityLink
                                communityId={communityId}
                                name={communityName}
                            />
                        </div>
                    ) : null}
                    {clientName && placementId ? (
                        <div>
                            <ClientLink clientId={placementId} name={clientName} />
                        </div>
                    ) : null}
                </div>
                <UserSelect
                    label="Assigned to"
                    value={state.tour?.userId as SelectableUser}
                    includeEveryone={false}
                    onChange={userId => {
                        if (userId) {
                            handleFieldChange("userId", userId as string);
                        }
                    }}
                />
                <div>
                    <label className="block mb-1">Tour Date</label>
                    <input
                        type="date"
                        required
                        value={state.tour?.tourDate || ""}
                        onChange={e => {
                            if (e.target.value) {
                                handleFieldChange("tourDate", e.target.value);
                            }
                        }}
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                    />
                </div>

                <div>
                    <label className="block mb-1">Tour Time (optional)</label>
                    <input
                        type="time"
                        step={TOUR_TIME_GRANULARITY_MINUTES * 60}
                        value={state.tour?.tourTime || ""}
                        onChange={e =>
                            handleFieldChange("tourTime", e.target.value || null)
                        }
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                    />
                </div>

                <div>
                    <Select
                        label="Tour Duration"
                        disabled={!state.tour?.tourTime}
                        value={String(currentDurationMinutes)}
                        onChange={e => handleFieldChange("tourDuration", e.target.value)}>
                        {durationOptions.map(minutes => (
                            <option key={minutes} value={String(minutes)}>
                                {formatDuration(minutes)}
                            </option>
                        ))}
                    </Select>
                </div>

                {placementId ? (
                    <>
                        <AutosavingInput
                            label="Tour Notes (shared with family)"
                            multiLine={true}
                            initial={state.tour?.familyNotes || ""}
                            onCommit={value => handleFieldChange("familyNotes", value)}
                        />

                        <AutosavingInput
                            label="Internal Notes"
                            multiLine={true}
                            initial={state.tour?.privateNotes || ""}
                            onCommit={value => handleFieldChange("privateNotes", value)}
                        />
                        <Toggle
                            label="Show on family view"
                            checked={state.tour?.showOnFamilyView ?? true}
                            onChange={isChecked =>
                                handleFieldChange(
                                    "showOnFamilyView",
                                    isChecked ? "true" : "false",
                                )
                            }
                        />
                    </>
                ) : (
                    <AutosavingInput
                        label="Notes"
                        multiLine={true}
                        initial={state.tour?.privateNotes || ""}
                        onCommit={value => handleFieldChange("privateNotes", value)}
                    />
                )}

                {state.errorMessage ? (
                    <div className="text-red-600 text-sm">{state.errorMessage}</div>
                ) : null}

                <div className="flex justify-end items-center pt-4">
                    <div className="flex items-center space-x-2">
                        {state.closeRequested ? <Spinner /> : null}
                        <PrimaryButton
                            onClick={handleClose}
                            disabled={state.closeRequested}>
                            Close
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </Panel>
    );
};
